<template>

  <div id="edit-menu-settings">

    <div v-if="is_loading" class="loader">
      <div class="lds-ellipsis"><div></div><div></div><div></div><div></div></div>
    </div>

    <div class="settings-card" v-else>

      <router-link :to="`/${menuId()}`" class="back-link">← Back to menu</router-link>

      <h2>Menu settings</h2>

      <ErrorNotice v-if="error" :message="error" />

      <div class="field">
        <label class="field-label" for="settings-name">Menu name</label>
        <input id="settings-name" type="text" v-model="name" maxlength="40" />
      </div>

      <div class="field">
        <label class="field-label" for="settings-currency">Currency</label>
        <select id="settings-currency" v-model="currency">
          <option v-for="option in currencyOptions()" :key="option" :value="option">{{ option }}</option>
        </select>
      </div>

      <div class="field">
        <label class="field-label">Ordering</label>
        <div class="switch-row" role="switch" :aria-checked="ordering_on ? 'true' : 'false'" tabindex="0"
          @click="ordering_on = !ordering_on" @keydown.space.prevent="ordering_on = !ordering_on" @keydown.enter.prevent="ordering_on = !ordering_on">
          <span class="switch" :class="{ 'switch-on': ordering_on }"></span>
          <span>{{ ordering_on ? 'Guests can order from this menu' : 'Ordering is off (no order buttons)' }}</span>
        </div>
        <p class="hint">Dine-in, pickup, delivery and in-room ordering are set up in the ordering settings.</p>
      </div>

      <div class="field">
        <div class="languages-header">
          <label class="field-label">Languages <span class="selected-count">({{ selected_languages.length }} selected)</span></label>
          <input type="search" class="language-search" v-model="language_search" placeholder="Search languages" />
        </div>
        <div v-for="group in languageGroups()" :key="group.title" class="language-group">
          <p class="group-title" v-if="group.languages.length > 0 && !group.collapsible">{{ group.title }}</p>
          <div class="group-toggle" v-if="group.collapsible && group.languages.length > 0" role="button" tabindex="0"
            @click="show_more_languages = !show_more_languages" @keydown.enter="show_more_languages = !show_more_languages">
            {{ group.title }} ({{ group.languages.length }}) <span class="chevron">{{ moreLanguagesOpen() ? '▴' : '▾' }}</span>
          </div>
          <div class="language-chips" v-if="!group.collapsible || moreLanguagesOpen()">
            <div class="language-chip" v-for="language in group.languages" :key="language.code"
              :class="{ 'language-chip-selected': selected_languages.includes(language.code) }"
              role="checkbox" :aria-checked="selected_languages.includes(language.code)" tabindex="0"
              @click="toggleLanguage(language.code)" @keydown.enter="toggleLanguage(language.code)">
              {{ language.name }} <span class="native-name" v-if="language.native !== language.name">{{ language.native }}</span>
            </div>
          </div>
        </div>
        <p class="hint" v-if="selected_languages.length === 0">Choose at least one language.</p>
      </div>

      <!-- order button look (menu.order_button_style); unset options keep the original button -->
      <div class="field">
        <label class="field-label">Order button</label>

        <div class="button-preview">
          <div class="preview-state">
            <div class="preview-button" :style="previewCss(button.label === 'text')">{{ button.label === 'text' ? previewLabel() : '+' }}</div>
            <span>Before adding</span>
          </div>
          <div class="preview-state">
            <div class="preview-stepper">
              <div class="preview-button" :style="previewCss(false)">-</div>
              <span class="preview-quantity">1</span>
              <div class="preview-button" :style="previewCss(false)">+</div>
            </div>
            <span>After adding</span>
          </div>
        </div>

        <div class="option-row" v-for="option in buttonOptions" :key="option.key">
          <span class="option-name">{{ option.name }}</span>
          <div class="segmented">
            <div v-for="choice in option.choices" :key="choice.value" class="segment"
              :class="{ 'segment-selected': button[option.key] === choice.value }" @click="button[option.key] = choice.value">{{ choice.label }}</div>
          </div>
        </div>

        <div class="option-row">
          <span class="option-name">Colour</span>
          <div class="colour-choice">
            <label class="inline-check"><input type="checkbox" v-model="button_uses_item_colour" /> Use item text colour</label>
            <input type="color" v-model="button.colour" v-if="!button_uses_item_colour" />
          </div>
        </div>

        <div class="option-row" v-if="button.fill === 'filled'">
          <span class="option-name">Text colour</span>
          <input type="color" v-model="button.text_colour" />
        </div>

        <div class="button-labels" v-if="button.label === 'text'">
          <p class="group-title">Button text (leave blank for the default)</p>
          <div class="label-row" v-for="code in selected_languages" :key="code">
            <span>{{ languageName(code) }}</span>
            <input type="text" maxlength="24" v-model="button.text[code]" :placeholder="defaultLabel(code)" :dir="isRtl(code) ? 'rtl' : null" />
          </div>
        </div>
      </div>


      <div class="actions">
        <button class="secondary" @click="backToMenu()" :disabled="saving">Cancel</button>
        <button @click="save()" :disabled="saving || !canSave()">{{ saving ? 'Saving…' : 'Save' }}</button>
      </div>

      <p class="legacy-link" @click="useLegacySettings()">Use legacy menu settings</p>

    </div>

  </div>

</template>


<script>
import ErrorNotice from '@/components/ai/ErrorNotice'
import { COLUMN_LANGUAGES, EXTRA_LANGUAGES, englishName, isRtl } from '@/languages.js'
import { ORDER_BUTTON_OPTIONS, normaliseOrderButtonStyle, orderButtonCss, orderButtonLabel } from '@/components/orderButtonStyle.js'

// The same currency names as the legacy settings steps (StepFour); languages from src/languages.js.
const CURRENCIES = ['USD', 'Pound Sterling', 'Rupiah', 'Baht', 'Ringgit', 'Singapore Dollar', 'Euros', 'Qatari Riyals', 'Omani Rial', 'Indian rupee', 'Philippine Peso', 'Kroner', 'Japanese Yen', 'Turkish Lira', 'Swiss Franc', 'Canadian Dollar', 'Hong Kong Dollar', 'New Zealand Dollar', 'Russian Ruble', 'Brazilian Real', 'Argentine Peso', 'Mexican Peso', 'South African Rand', 'Egyptian Pound', 'Saudi Riyal', 'United Arab Emirates Dirham', 'Pakistani Rupee', 'South Korean Won', 'New Taiwan Dollar', 'Romanian Leu'].sort()
// the menu flags that each turn on a kind of ordering (dine-in, delivery, pickup, in-room)
const ORDER_TYPES = ['orderable', 'orders_for_delivery', 'orders_for_pickup', 'orders_for_in_room']

const byName = (a, b) => a.name.localeCompare(b.name)
// the original languages (on/off columns on the menu) and the newer ones (menu.extra_languages)
const LANGUAGES = [...COLUMN_LANGUAGES].sort(byName)
const MORE_LANGUAGES = [...EXTRA_LANGUAGES].sort(byName)
const ALL_CODES = [...LANGUAGES, ...MORE_LANGUAGES].map(language => language.code)

// Single-page menu settings: name, currency and languages. The step-by-step settings stay available
// as "legacy menu settings" (/:id/edit_legacy), which also has the outlet and menu type options.
export default {
  components: { ErrorNotice },
  name: 'EditMenuSettings',
  data() {
    return {
      is_loading: true,
      name: '',
      currency: 'Not specified',
      original_currency: null,
      selected_languages: [],
      language_search: '',
      show_more_languages: false,
      // Ordering: on if any order type is; turning it off switches them all off, turning it back on restores
      // the types that were on (or dine-in if none were). Untouched, the order types aren't sent at all.
      ordering_on: false,
      initial_ordering_on: false,
      initial_order_types: {},
      button: normaliseOrderButtonStyle({}),
      button_uses_item_colour: true,
      buttonOptions: [
        { key: 'shape', name: 'Shape', choices: ORDER_BUTTON_OPTIONS.shape },
        { key: 'fill', name: 'Style', choices: ORDER_BUTTON_OPTIONS.fill },
        { key: 'size', name: 'Size', choices: ORDER_BUTTON_OPTIONS.size },
        { key: 'label', name: 'Shows', choices: ORDER_BUTTON_OPTIONS.label }
      ],
      saving: false,
      error: ''
    }
  },
  created() {
    this.$http.secured.get(`/app_edit_menu/${this.menuId()}`)
      .then(response => {
        const menu = response.data.menu
        this.name = menu.name || ''
        this.currency = menu.currency || 'Not specified'
        this.original_currency = this.currency
        this.selected_languages = (response.data.languages || []).filter(code => ALL_CODES.includes(code))
        this.show_more_languages = this.selected_languages.some(code => MORE_LANGUAGES.some(language => language.code === code))
        this.initial_order_types = Object.fromEntries(ORDER_TYPES.map(type => [type, menu[type] === true]))
        this.ordering_on = Object.values(this.initial_order_types).some(Boolean)
        this.initial_ordering_on = this.ordering_on
        this.button = normaliseOrderButtonStyle(menu.order_button_style)
        this.button_uses_item_colour = !menu.order_button_style || !menu.order_button_style.colour
        if (!this.button.colour) {
          this.button.colour = '#000000'
        }
        this.is_loading = false
      })
      .catch(() => {
        this.is_loading = false
        this.error = "Couldn't load this menu's settings."
      })
  },
  methods: {
    menuId() {
      return this.$router.currentRoute.params.id
    },
    // Keeps a currency that isn't in the list (e.g. an older spelling) so saving doesn't change it.
    currencyOptions() {
      const options = ['Not specified', ...CURRENCIES]
      return this.original_currency && !options.includes(this.original_currency) ? [this.original_currency, ...options] : options
    },
    isRtl,
    languageName(code) {
      return englishName(code)
    },
    defaultLabel(code) {
      return orderButtonLabel({}, code, this.$translations)
    },
    previewLabel() {
      const language = this.selected_languages[0] || 'en'
      return orderButtonLabel(this.button, language, this.$translations)
    },
    previewCss(wide) {
      return orderButtonCss(this.buttonStyleToSave(), '#000000', wide)
    },
    orderTypesToSave() {
      if (this.ordering_on === this.initial_ordering_on) {
        return {}
      }
      if (!this.ordering_on) {
        return Object.fromEntries(ORDER_TYPES.map(type => [type, false]))
      }
      const anyBefore = Object.values(this.initial_order_types).some(Boolean)
      return anyBefore ? { ...this.initial_order_types } : { orderable: true }
    },
    // What gets stored: only the chosen options (the colour only when it isn't following the item text).
    buttonStyleToSave() {
      const text = Object.fromEntries(Object.entries(this.button.text || {}).filter(([code, label]) => this.selected_languages.includes(code) && label && label.trim()))
      return {
        shape: this.button.shape, fill: this.button.fill, size: this.button.size, label: this.button.label,
        text_colour: this.button.text_colour,
        ...(this.button_uses_item_colour ? {} : { colour: this.button.colour }),
        ...(Object.keys(text).length > 0 ? { text } : {})
      }
    },
    languageGroups() {
      const query = this.language_search.trim().toLowerCase()
      const matches = language => !query || language.name.toLowerCase().includes(query) || language.native.toLowerCase().includes(query) || language.code === query
      return [
        { title: 'Languages', languages: LANGUAGES.filter(matches) },
        { title: 'More languages', languages: MORE_LANGUAGES.filter(matches), collapsible: true }
      ]
    },
    // Shown when opened, when searching, or (on load) when the menu already uses one of these languages.
    moreLanguagesOpen() {
      return this.show_more_languages || this.language_search.trim() !== ''
    },
    toggleLanguage(code) {
      this.selected_languages = this.selected_languages.includes(code)
        ? this.selected_languages.filter(selected => selected !== code)
        : [...this.selected_languages, code]
    },
    canSave() {
      return this.name.trim() !== '' && this.selected_languages.length > 0
    },
    save() {
      if (!this.canSave()) {
        return
      }
      const name = this.name.trim()
      // on/off flags for the original languages; the newer ones go in extra_languages
      const languageFlags = Object.fromEntries(LANGUAGES.map(({ code }) => [code, this.selected_languages.includes(code)]))
      languageFlags.extra_languages = MORE_LANGUAGES.map(({ code }) => code).filter(code => this.selected_languages.includes(code))
      this.error = ''
      this.saving = true
      this.$http.secured.post(`/app_update_menu/${this.menuId()}`, { menu: { name, restaurant_name: name, currency: this.currency, ...languageFlags, ...this.orderTypesToSave(), order_button_style: this.buttonStyleToSave() } })
        .then(() => this.backToMenu())
        .catch(error => {
          this.saving = false
          const data = (error.response && error.response.data) || {}
          const messages = Object.values(data).flat().filter(message => typeof message === 'string')
          this.error = messages[0] || "Couldn't save the settings. Please try again."
        })
    },
    backToMenu() {
      this.$router.replace(`/${this.menuId()}`)
    },
    useLegacySettings() {
      this.$router.replace(`/${this.menuId()}/edit_legacy`)
    }
  }
}
</script>


<style scoped>

#edit-menu-settings {
  box-sizing: border-box;
  padding: 120px 20px 80px 20px;
  min-height: 100vh;
  background-color: #f4f4f4;
}

.settings-card {
  box-sizing: border-box;
  max-width: 640px;
  margin: 0px auto;
  padding: 30px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.back-link {
  font-size: 12.5px;
  font-weight: 600;
  color: dimgray;
  text-decoration: none;
}

h2 {
  font-size: 26px;
  font-weight: 400;
  margin: 15px 0px 25px 0px;
}

.field {
  margin-bottom: 22px;
}

.field-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: dimgray;
  margin-bottom: 8px;
}

input[type="text"], select {
  box-sizing: border-box;
  width: 100%;
  font-family: 'Montserrat', sans-serif;
  font-size: 15px;
  padding: 10px 12px;
  border: 1px solid #dddddd;
  border-radius: 6px;
  background: white;
}

.languages-header {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
}

.languages-header .field-label {
  margin-bottom: 0px;
}

.selected-count {
  text-transform: none;
  letter-spacing: 0px;
  font-weight: 400;
}

.language-search {
  font-family: 'Montserrat', sans-serif;
  font-size: 13px;
  padding: 6px 10px;
  border: 1px solid #dddddd;
  border-radius: 6px;
  width: 200px;
}

.language-group {
  margin-bottom: 12px;
}

.group-title {
  font-size: 12px;
  color: dimgray;
  margin: 8px 0px 6px 0px;
}

.button-preview {
  display: flex;
  flex-direction: row;
  gap: 40px;
  padding: 16px;
  background: #f7f7f7;
  border-radius: 8px;
  margin-bottom: 14px;
}

.preview-state {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.preview-state > span {
  font-size: 11px;
  color: dimgray;
}

.preview-stepper {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.preview-button {
  display: flex;
  align-items: center;
  justify-content: center;
  border-width: 1px;
  border-style: solid;
}

.preview-quantity {
  font-size: 13px;
  font-weight: 600;
}

.option-row {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.option-name {
  font-size: 13px;
  width: 90px;
}

.segmented {
  display: flex;
  flex-direction: row;
  border: 1px solid #dddddd;
  border-radius: 6px;
  overflow: hidden;
}

.segment {
  font-size: 13px;
  padding: 6px 12px;
  cursor: pointer;
  border-right: 1px solid #dddddd;
}

.segment:last-child {
  border-right: 0px;
}

.segment-selected {
  background: #111111;
  color: white;
}

.colour-choice {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
}

.inline-check {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  cursor: pointer;
  margin: 0px;
}

input[type="color"] {
  width: 36px;
  height: 30px;
  padding: 0px;
  border: 1px solid #dddddd;
  border-radius: 4px;
}

.button-labels {
  margin-top: 6px;
}

.label-row {
  display: grid;
  grid-template-columns: 140px 1fr;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
  font-size: 13px;
}

.label-row input {
  padding: 7px 10px;
  font-size: 13px;
}

.switch-row {
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  cursor: pointer;
  user-select: none;
}

.switch {
  position: relative;
  flex-shrink: 0;
  width: 40px;
  height: 22px;
  border-radius: 999px;
  background: #cccccc;
  transition: background-color 0.15s;
}

.switch::after {
  content: '';
  position: absolute;
  top: 3px;
  left: 3px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: white;
  transition: transform 0.15s;
}

.switch-on {
  background: #39B54A;
}

.switch-on::after {
  transform: translateX(18px);
}

.switch-row:focus-visible .switch {
  outline: 2px solid #276175;
  outline-offset: 2px;
}

.group-toggle {
  display: inline-block;
  font-size: 13px;
  font-weight: 600;
  color: #276175;
  cursor: pointer;
  margin: 8px 0px 6px 0px;
  user-select: none;
}

.chevron {
  font-size: 11px;
}

.language-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.language-chip {
  font-size: 13px;
  padding: 7px 12px;
  border: 1px solid #dddddd;
  border-radius: 999px;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.15s, border-color 0.15s;
}

.language-chip-selected {
  background-color: #111111;
  border-color: #111111;
  color: white;
}

.native-name {
  opacity: 0.6;
  margin-left: 4px;
}

.hint, .error {
  font-size: 12.5px;
  margin: 8px 0px 0px 0px;
}

.error {
  color: #BE1E2D;
  font-weight: 600;
}

.actions {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 10px;
}

button {
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 22px;
  border-radius: 6px;
  border: 1px solid #111111;
  background: #111111;
  color: white;
  cursor: pointer;
}

button.secondary {
  background: white;
  color: #111111;
  border-color: #cccccc;
}

button:disabled {
  opacity: 0.5;
  cursor: default;
}

.legacy-link {
  font-size: 12.5px;
  font-weight: 600;
  color: dimgray;
  text-decoration: underline;
  cursor: pointer;
  width: max-content;
  margin: 25px 0px 0px 0px;
}

@media(max-width: 600px) {
  #edit-menu-settings {
    padding: 90px 12px 60px 12px;
  }
  .settings-card {
    padding: 20px;
  }
}

</style>
